import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { PageHeader, Empty } from "../../components/common";
import { RiskPill, COMPANY, fmtD, useLoad, useOpsMeta, inp, btn, btn2 } from "../../lib/ops";
import { PlanStart } from "../../components/ops/PlanStart";

const CHIPS = [["", "All"], ["late", "Late"], ["blocked", "Blocked"], ["at_risk", "At risk"], ["on_track", "On track"], ["missing", "No update"], ["not_planned", "No steps yet"], ["done", "Delivered"]];

const Progress = ({ v }) => (
  <div className="flex items-center gap-2 min-w-[110px]"><div className="h-1.5 bg-muted flex-1"><div className="h-1.5 bg-primary" style={{ width: `${v}%` }} /></div><span className="num text-xs">{v}%</span></div>
);

const Flags = ({ r }) => (
  <div className="flex flex-wrap gap-1 text-[11px]">
    {r.late_tasks > 0 && <span className="text-rose-600">{r.late_tasks} late</span>}
    {r.update_missing > 0 && <span className="text-amber-700">{r.update_missing} no update</span>}
    {r.plan_after_commitment && <span className="text-orange-700">plan ends after commitment</span>}
    {r.prepo_pending && <span className="text-amber-700" data-testid={`ops-prepo-pending-${r.uid}`}>Pre-PO waiting for approval</span>}
  </div>
);

const Card = ({ r }) => (
  <Link to={`/ops/uid/${r.uid}`} className="ax-panel p-4 block hover:border-primary" data-testid={`ops-card-${r.uid}`}>
    <div className="flex justify-between gap-2"><div className="min-w-0"><div className="font-mono text-xs">{r.uid}</div><div className="font-medium truncate">{r.client_name}</div></div><RiskPill r={r.risk} /></div>
    <div className="text-xs text-muted-foreground mt-1 truncate">{r.description}</div>
    <div className="mt-2"><Progress v={r.progress} /></div>
    <div className="text-xs mt-2">Commitment {fmtD(r.commitment)} · Forecast {fmtD(r.forecast_end)}</div>
    {r.pending_with.length > 0 && <div className="text-xs text-muted-foreground mt-1">With {r.pending_with.join(", ")}</div>}
    <Flags r={r} />
  </Link>
);

const Table = ({ rows }) => (
  <div className="ax-panel ax-scroll hidden md:block"><table className="ax-table w-full min-w-[1000px]" data-testid="ops-board-table">
    <thead><tr><th>UID</th><th>Customer</th><th>Company</th><th>Progress</th><th>Pending with</th><th>Forecast</th><th>Commitment</th><th>Status</th><th>Flags</th></tr></thead>
    <tbody>{rows.map((r) => (
      <tr key={r.uid} data-testid={`ops-row-${r.uid}`}>
        <td><Link to={`/ops/uid/${r.uid}`} className="font-mono text-xs text-primary hover:underline" data-testid={`ops-row-link-${r.uid}`}>{r.uid}</Link><div className="text-[11px] text-muted-foreground">{(r.record_class || "").replace("_", "-")}</div></td>
        <td className="max-w-[260px]"><div className="truncate font-medium">{r.client_name}</div><div className="truncate text-xs text-muted-foreground">{r.description}</div></td>
        <td className="text-xs">{COMPANY[r.company]}</td>
        <td><Progress v={r.progress} /><div className="text-[11px] text-muted-foreground">{r.done}/{r.processes} steps</div></td>
        <td className="text-xs max-w-[180px]">{r.pending_with.join(", ") || "—"}</td>
        <td className="text-xs whitespace-nowrap">{fmtD(r.forecast_end)}</td>
        <td className="text-xs whitespace-nowrap">{fmtD(r.commitment)}</td>
        <td><RiskPill r={r.risk} tid={`ops-risk-${r.uid}`} /></td>
        <td><Flags r={r} /></td>
      </tr>))}</tbody>
  </table></div>
);

export default function OpsBoard() {
  const [sp, setSp] = useSearchParams();
  const meta = useOpsMeta();
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [start, setStart] = useState(false);
  const risk = sp.get("risk") || "";
  const company = sp.get("company") || "";
  const qs = new URLSearchParams({ ...(risk && { risk }), ...(company && { company }), ...(term && { q: term }) }).toString();
  const [d] = useLoad(`/ops/board?${qs}`);
  const setP = (k, v) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n); };
  const counts = d?.counts || {};
  const all = Object.entries(counts).filter(([k]) => k !== "missing").reduce((a, [, v]) => a + v, 0);
  return (
    <div>
      <PageHeader eyebrow="Operations" title="Production board" desc="Every planned UID with its live progress, who it is waiting on, and whether it will meet the customer commitment.">
        {meta?.can.plan && <button data-testid="ops-plan-start-button" onClick={() => setStart(true)} className={`${btn} flex items-center gap-1`}><Plus className="h-4 w-4" />Plan a UID</button>}
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-3" data-testid="ops-risk-chips">
        {CHIPS.map(([k, l]) => (
          <button key={k || "all"} data-testid={`ops-chip-${k || "all"}`} onClick={() => setP("risk", k)} className={`h-8 px-3 text-xs border ${risk === k ? "border-primary bg-primary/10 text-foreground" : "text-muted-foreground"}`}>
            {l} <span className="num ml-1">{k ? counts[k] || 0 : all}</span></button>))}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <form onSubmit={(e) => { e.preventDefault(); setTerm(q.trim()); }} className="flex gap-2 flex-1 min-w-[240px]">
          <input data-testid="ops-board-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search UID, customer, description" className={inp} />
          <button data-testid="ops-board-search-button" className={btn2}>Search</button>
        </form>
        <select data-testid="ops-board-company" value={company} onChange={(e) => setP("company", e.target.value)} className={`${inp} w-auto`}>
          <option value="">Both branches</option>{Object.entries(COMPANY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
      </div>
      {!d ? <div className="text-sm text-muted-foreground">Loading…</div> : !d.items.length ? <Empty text={meta?.can.plan ? "No UIDs here. Use “Plan a UID” to start one." : "No UIDs here yet."} /> : (
        <>
          <Table rows={d.items} />
          <div className="grid gap-3 md:hidden" data-testid="ops-board-cards">{d.items.map((r) => <Card key={r.uid} r={r} />)}</div>
          {d.total > d.items.length && <p className="text-xs text-muted-foreground mt-2">Showing {d.items.length} of {d.total}. Narrow the search to see more.</p>}
        </>)}
      {start && meta && <PlanStart meta={meta} onClose={() => setStart(false)} />}
    </div>
  );
}
